<template>
  <div class="odsa-step-4">
    <div class="card">
      <div class="odsa-step-4__content">
        <h3 class="h4">
          {{ i18n.tv('Frontend.Title.Result.DesignSpace', 'ORingConfiguratorImport') }}
        </h3>
        <div class="button-container">
          <div class="button-container--start">
            <ReactiveButton
              :is-primary-button="false"
              :button-text="i18n.tv('Frontend.PdfDownload.DownloadPdf', 'ORingConfiguratorImport')"
              @click="openConstructionPopup('download')"
            />
            <ReactiveButton
              :is-primary-button="false"
              :button-text="i18n.tv('Frontend.PdfDownload.SendPdf', 'ORingConfiguratorImport')"
              @click="openConstructionPopup('send')"
            />
          </div>
          <div class="button-container--end">
            <ReactiveButton
              :is-primary-button="false"
              @click="goToDesignCockpit"
            >
              {{ i18n.tv('goToDesignCockpit', 'ORingConfigurator') }}
              <MdArrowForward class="forward-icon" />
            </ReactiveButton>
          </div>
        </div>
        <div class="odsa-step-4__content__lower-half">
          <div class="calculated-items">
            <div
              v-for="(rowPair, index) in mappedResult.data"
              :key="index"
              class="calculated-item"
            >
              <div class="calculated-item__row">
                <span class="calculated-item__label">{{ rowPair.labelLeft }}</span>
                <span class="calculated-item__value">{{ rowPair.valueLeft }}</span>
              </div>
              <div class="line-separator">
                <div />
              </div>
              <div class="calculated-item__row row-right-side">
                <span class="calculated-item__label">{{ rowPair.labelRight }}</span>
                <span class="calculated-item__value">{{ rowPair.valueRight }}</span>
              </div>
            </div>
          </div>
          <div class="image-container">
            <img loading="lazy"
              :src="mappedResult.image"
              alt="O-Ring Dimenstions D1 and D2 Image"
              class="oring-design-space-image"
            >
          </div>
        </div>
      </div>
    </div>
    <PopupORingConstruction
      :show="showConstructionPopup"
      :mode="constructionMode"
      :initial-data="constructionFormData"
      :on-submit="handleConstructionSubmit"
      @close-pop-up="showConstructionPopup = false"
    />
  </div>
</template>

<script setup lang="ts">
import useI18nExtended from '~/composables/usei18nExtended'
import {useORingCalculatorStore} from '~/store/oring-calculator'
import {storeToRefs} from 'pinia'
import designSpaceAxialImg from '~/assets/images/O-Ring_axial_DesignSpace_Marks.png'
import designSpaceRadialGeometry8Img from '~/assets/images/O-Ring_radial-inBore_DesignSpace_Marks.png'
import designSpaceRadialGeometry9Img from '~/assets/images/O-Ring_radial_inShaft_DesignSpace_Marks.png'
import PopupORingConstruction from '~/components/popup/PopupORingConstruction.vue'
import {ref} from 'vue'
import {useDesignSpaceHelper} from '~/composables/oring/useDesignSpaceHelper'
import MdArrowForward from '~/assets/icons/md_arrow_forward.svg?component'
import {handleConstructionSubmit as helperHandleConstructionSubmit} from '~/composables/oring-helper'

const app = useNuxtApp()
const i18n = useI18nExtended()
const {availableResults} = useDesignSpaceHelper()
const oRingCalculatorStore = useORingCalculatorStore()
const {
  oringAssistantGeometryId,
  oringDesignSpaceCurrentHousing
} = storeToRefs(oRingCalculatorStore)

const showConstructionPopup = ref(false)
const constructionMode = ref<'download' | 'send'>()
const constructionFormData = ref({
  firstName: '',
  lastName: '',
  company: '',
  email: '',
  designRemarks: '',
  pdfLanguage: i18n.locale.value
})

const mappedResult = computed(() => {
  if (oringDesignSpaceCurrentHousing.value === null) {
    return {data: [], image: null}
  }

  if (oringAssistantGeometryId.value === 8) {
    return mapResults(availableResults.value.designSpaceRadialInShaft, designSpaceRadialGeometry8Img)
  }

  if (oringAssistantGeometryId.value === 9) {
    return mapResults(availableResults.value.designSpaceRadialInBore, designSpaceRadialGeometry9Img)
  }

  return mapResults(availableResults.value.designSpaceAxial, designSpaceAxialImg)
})

const mapResults = (items: any, image: any) => {
  const dimensionUnit = oRingCalculatorStore.oringCalculatorImperial
    ? i18n.tv('inch', 'unitsOfMeasure')
    : i18n.tv('mm', 'unitsOfMeasure')

  const data = items.map((item: any) => {
    const housing: any = oringDesignSpaceCurrentHousing.value as any
    const entry: any = housing ? housing[item.id] : null

    // Keep original labels (with unit in brackets) untouched
    const labelLeft: string = item.labelLeft
    const labelRight: string = item.labelRight
    let valueLeft: any = entry?.val ?? null
    let valueRight: any = item.showNumberValue ? entry?.toleranceMax : entry?.toleranceClassName

    // Grab unit from labelLeft if present but DO NOT remove from label
    if (typeof valueLeft === 'number') {
      valueLeft = parseFloat(valueLeft.toFixed(3)) + ` ${dimensionUnit}`
    }
    if (valueRight && typeof valueRight === 'number') {
      valueRight = `+ ${valueRight.toFixed(3)} ${dimensionUnit}`
    }

    return {...item, labelLeft, labelRight, valueLeft, valueRight}
  })

  return {data, image}
}

const handleConstructionSubmit = async (formData: {
  name: string,
  company: string,
  email: string,
  designRemarks: string,
  pdfLanguage: string | { label: string, value: string },
  mode: 'download' | 'send'
}) => {
  await helperHandleConstructionSubmit({
    mode: formData.mode,
    popupData: {
      name: formData.name,
      company: formData.company,
      email: formData.email,
      designRemarks: formData.designRemarks,
      pdfLanguage: formData.pdfLanguage
    },
    housingType: 'designSpace',
    customHousingData: oringDesignSpaceCurrentHousing.value
  })
}

const openConstructionPopup = (mode: 'download' | 'send') => {
  constructionMode.value = mode
  showConstructionPopup.value = true
}

const goToDesignCockpit = () => {
  oRingCalculatorStore.transferToDesignCockpit(true)
  const path = app.$globalization.localePath('/oring-calculator/design-cockpit', i18n.locale.value, app.$globalization.getRegion())
  ;(app as any).$router.push(path)
}
</script>

<style lang="scss">
.odsa-step-4 {
  margin-bottom: 1rem;
  display: flex;

  .card {
    padding: 1rem;
    box-shadow: $shadow-default;
    margin: 1.25rem 0;
    width: 100%;

     @include breakpoint-down($lg) {
      width: calc(100% - 2rem);
    }
  }

  &__content {
    .h4 {
      margin: 0 0 1rem;
    }

    .button-container {
      display: flex;
      justify-content: space-between;

      &--start {
        display: flex;
        justify-content: flex-start;
        gap: 1rem;

        @include breakpoint-down($md) {
          display: flex;
          flex-direction: column;
          width: 11.375rem;
          gap: 1rem;
        }
      }

      &--end {
        display: flex;
        justify-content: flex-end;

        .forward-icon {
          width: 1.25rem;
          height: 1.25rem;
          margin-left: 0.25rem;
          flex-shrink: 0;
        }
      }

       @include breakpoint-down($md) {
          display: flex;
          flex-direction: column;
          gap: 1rem;

          &--end {
            justify-content: flex-start;
          }
        }
    }

    &__lower-half {
      display: flex;
      justify-content: flex-start;
      gap: 1rem;
      margin-top: 1.5rem;

      .calculated-items {
        display: flex;
        flex-direction: column;
        gap: 1rem;

        .calculated-item {
          display: flex;
          justify-content: space-between;

          &__row {
            display: flex;
            flex-direction: column;
            width: 15.625rem;
          }

          .row-right-side {
            margin-left: .5rem;
          }

          &__label {
            font-weight: $base-text-font-weight-light;
            margin-bottom: .5rem;
          }

          .line-separator {
            display: flex;
            align-content: center;
            flex-wrap: wrap;

            div {
              height: 0.125rem;
              width: 1.25rem;
              background-color: $color__fst__dark__gray;
              margin: 0.5rem 0;
            }
          }
        }

         @include breakpoint-down($md) {
          display: flex;
          flex-direction: column;
        }
      }

      .image-container {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      @include breakpoint-down($lg) {
        flex-direction: column;
        align-items: stretch;
        gap: 2rem;
      }
    }
  }
}
</style>
